/**
 * Macro recording state management.
 * Captures API interactions for export as Python automation scripts.
 *
 * Separate from main store.js to keep macro concerns isolated.
 */

// Macro state structure
const macroState = {
  isRecording: false,
  actions: [],      // Array of recorded action objects
  startedAt: null   // Timestamp when recording started
};

// Subscribers set
const listeners = new Set();

/**
 * Subscribe to macro state changes.
 * @param {Function} fn - Callback invoked on state change
 * @returns {Function} Unsubscribe function
 */
export function subscribeMacro(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Notify all listeners of state change.
 */
function notifyListeners() {
  for (const listener of listeners) {
    try {
      listener(macroState);
    } catch (error) {
      console.error('Macro state listener error:', error);
    }
  }
}

/**
 * Get the current macro state (read-only view).
 * @returns {Object} Current macro state
 */
export function getMacroState() {
  return {
    isRecording: macroState.isRecording,
    actions: [...macroState.actions],
    startedAt: macroState.startedAt
  };
}

/**
 * Check if recording is active.
 * @returns {boolean} True if recording
 */
export function isRecording() {
  return macroState.isRecording;
}

/**
 * Get the recorded actions array.
 * @returns {Array} Copy of recorded actions
 */
export function getRecordedActions() {
  return [...macroState.actions];
}

/**
 * Start recording macro actions.
 * Clears any previous recording and sets startedAt timestamp.
 */
export function startRecording() {
  macroState.isRecording = true;
  macroState.actions = [];
  macroState.startedAt = Date.now();
  notifyListeners();
}

/**
 * Stop recording macro actions.
 * Keeps the recorded actions for preview/export.
 */
export function stopRecording() {
  macroState.isRecording = false;
  notifyListeners();
}

/**
 * Clear the recording completely.
 * Stops recording and removes all captured actions.
 */
export function clearRecording() {
  macroState.isRecording = false;
  macroState.actions = [];
  macroState.startedAt = null;
  notifyListeners();
}

/**
 * Clear recorded actions without stopping recording.
 * Resets the actions array and startedAt timestamp but keeps recording active.
 */
export function clearActions() {
  macroState.actions = [];
  macroState.startedAt = Date.now();
  notifyListeners();
}

/**
 * Add a recorded action to the macro.
 * Only adds if recording is active.
 *
 * @param {Object} action - Parsed action from recorder
 * @param {string} action.id - Unique action ID
 * @param {string} action.operation - Operation type (put_payload, get_payload, mark_complete)
 * @param {string} action.method - HTTP method
 * @param {string} action.path - API path
 * @param {Object} action.params - Parsed path parameters (sampleId, stageId, workflowId, readerId)
 * @param {boolean} action.needsEncryption - Whether this operation requires encryption
 * @param {boolean} action.success - Whether request succeeded
 * @param {number} action.status - HTTP status code
 * @param {string|null} action.error - Error message if failed
 */
export function addRecordedAction(action) {
  if (!macroState.isRecording) {
    return;
  }

  const recordedAction = {
    ...action,
    timestamp: Date.now()
  };

  macroState.actions.push(recordedAction);
  notifyListeners();
}
